import { spanOf, type CoverCell, type PeriodKind } from './periods';

export type FunctionExpandContext = {
  mode: 'validate' | 'compile';
  currentPeriodId: string;
  cover: CoverCell[];
};

export type FormulaFunctionSpec = {
  name: string;

  // how to find calls in a formula (stored as strings so FE/BE can both use it)
  pattern: string;   // regex source (no / /)
  flags: string; //e.g. "gi"
  // validate captured groups
  validate: (groups: string[]) => boolean;
  explain?: (groups: string[]) => string;

  // backend-only: how to expand into plain math
  expand?: (groups: string[], ctx: FunctionExpandContext) => string;

  // formula-editor help (frontend only)
  help?: {
    signature: string;    // e.g. "rollsum({account}, n)"
    description: string;  // one or two sentences, shown under the signature
    example?: string;     // e.g. "rollsum({4000}, 3)"
    template: string;     // inserted text; "|" marks where the cursor is placed
  };
};

// ---- DEPEQPD support --------------------------------------------------------

const UNIT_MONTHS: Record<string, number> = {
  m: 1, mo: 1, mos: 1, month: 1, months: 1,
  q: 3, qtr: 3, quarter: 3, quarters: 3,
  y: 12, yr: 12, yrs: 12, year: 12, years: 12,
};

type Convention = 'primo' | 'medio' | 'ultimo';
const CONVENTIONS: Convention[] = ['primo', 'medio', 'ultimo'];

/** Month within the investment column where depreciation begins. */
const startOffsetIn = (kind: PeriodKind, conv: Convention): number => {
  if (kind === 'M') return 0;
  if (kind === 'Q') return conv === 'primo' ? 0 : conv === 'medio' ? 1 : 2;
  return conv === 'primo' ? 0 : conv === 'medio' ? 6 : 12; // month 1 / 6 / 12 accounting-standard half-year and next-year-start
};

export const MONTHS_PER_KIND: Record<PeriodKind, number> = { M: 1, Q: 3, Y: 12 };


export const FUNCTION_SPECS: FormulaFunctionSpec[] = [
  {
    name: 'rollsum',
    pattern: 'rollsum\\s*\\(\\s*(\\{[A-Za-z0-9_.]+\\})\\s*,\\s*(\\d+)\\s*\\)',
    flags: 'gi',
    validate: ([_acc, lenStr]) => {
      const len = Number(lenStr);
      return Number.isFinite(len) && len > 0;
    },
    expand: ([acc, lenStr], _ctx) => {
      const len = Number(lenStr);
      const parts: string[] = [];
      for (let i = 1; i <= len; i++) parts.push(`${acc}(-${i})`);
      return parts.length === 1 ? parts[0] : `(${parts.join('+')})`;
    },
    help: {
      signature: 'rollsum({account}, n)',
      description:
        'Sum of the account over the n periods before the current one (the current period is not included).',
      example: 'rollsum({4000}, 3)  =  {4000}(-1) + {4000}(-2) + {4000}(-3)',
      template: 'rollsum(|, 3)',
    },
  },




 {
  name: 'SUMEQPD',
  pattern: 'SUMEQPD\\s*\\(\\s*(\\{[A-Za-z0-9_.]+\\})\\s*,\\s*([-+]?\\d+)\\s*,\\s*([-+]?\\d+)\\s*\\)',
  flags: 'gi',
  validate: ([_acc, aStr, bStr]) => {
    const a = Number(aStr);
    const b = Number(bStr);
    return Number.isFinite(a) && Number.isFinite(b) && Number.isInteger(a) && Number.isInteger(b);
  },
  
  expand: ([acc, aStr, bStr], _ctx) => {
  let a = Number(aStr);
  let b = Number(bStr);
  if (a > b) [a, b] = [b, a];

  const parts: string[] = [];
  for (let k = a; k <= b; k++) {
    const off = k >= 0 ? `+${k}` : `${k}`;
    parts.push(`${acc}(${off})`);
  }
  return parts.length === 1 ? parts[0] : `(${parts.join('+')})`;
},
  help: {
    signature: 'SUMEQPD({account}, from, to)',
    description:
      'Sum of the account over a range of periods relative to the current one, both ends included. 0 is the current period, negative numbers are earlier periods, positive numbers are later ones.',
    example: 'SUMEQPD({4000}, -2, 0)  =  {4000}(-2) + {4000}(-1) + {4000}(+0)',
    template: 'SUMEQPD(|, -2, 0)',
  },
},

{
  name: 'DEPEQPD',
  // DEPEQPD({acc}, life [, unit] [, convention])
  pattern:
    'DEPEQPD\\s*\\(\\s*(\\{[A-Za-z0-9_.]+\\})\\s*,\\s*(\\d+)\\s*' +
    '(?:,\\s*([A-Za-z]+)\\s*)?(?:,\\s*([A-Za-z]+)\\s*)?\\)',
  flags: 'gi',

  validate: ([, lifeStr, unitStr, convStr]) => {
    const life = Number(lifeStr);
    if (!Number.isInteger(life) || life <= 0) return false;
    if (unitStr && !UNIT_MONTHS[unitStr.toLowerCase()]) return false;
    if (convStr && !CONVENTIONS.includes(convStr.toLowerCase() as Convention)) return false;
    return true;
  },

  explain: ([, lifeStr, unitStr, convStr]) => {
    const life = Number(lifeStr);
    if (!Number.isInteger(life) || life <= 0)
      return `useful life must be a positive whole number, got "${lifeStr}"`;
    if (unitStr && !UNIT_MONTHS[unitStr.toLowerCase()])
      return `unknown unit "${unitStr}" — use m, q or y`;
    return `unknown convention "${convStr}" — use primo, medio or ultimo`;
  },

  expand: ([acc, lifeStr, unitStr, convStr], ctx) => {
    const life = Number(lifeStr) * UNIT_MONTHS[(unitStr ?? 'm').toLowerCase()];
    const conv = ((convStr ?? 'primo').toLowerCase()) as Convention;

    const { m0: tStart, len } = spanOf(ctx.currentPeriodId);
    const tEnd = tStart + len;

    const terms: string[] = [];
    for (const c of ctx.cover) {
      if (c.m0 >= tEnd) break;                          // cover is sorted by m0
      const s = c.m0 + startOffsetIn(c.kind, conv);
      const overlap = Math.min(s + life, tEnd) - Math.max(s, tStart);
      if (overlap > 0) terms.push(`${overlap}*${acc}[${c.periodId}]`);
    }

    return terms.length === 0 ? '0' : `(-(${terms.join('+')})/${life})`;
  },

  help: {
    signature: 'DEPEQPD({account}, life [, unit] [, convention])',
    description:
      'Straight-line depreciation of the investments booked on the account, returned as a negative amount. ' +
      'unit is m, q or y (default m = months). convention sets when depreciation starts in the investment period: ' +
      'primo (start, default), medio (middle) or ultimo (end).',
    example: 'DEPEQPD({1200}, 5, y, medio)',
    template: 'DEPEQPD(|, 5, y)',
  },
},

];
